import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { LogIn, Mail, Lock, Shield, Users, MapPin, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";
import { useLogin } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import LogoMunicipal from "@/assets/logo_municipal.svg";
import toast from "react-hot-toast";
import { useState } from "react";
import { syncSentryUser } from "@/lib/sentryUser";

const loginSchema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(6, "Senha deve ter no mínimo 6 caracteres"),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const Login = () => {
  const navigate = useNavigate();
  const loginMutation = useLogin();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const response = await loginMutation.mutateAsync(data);

      localStorage.setItem("duas-vidas-token", response.access_token);
      localStorage.setItem("duas-vidas-user", JSON.stringify(response.user));
      syncSentryUser();

      toast.success("Login realizado com sucesso!");

      navigate("/home");
    } catch (error: any) {
      console.error("Erro no login:", error);
      const errorMessage =
        error?.response?.data?.message ||
        "Erro ao fazer login. Verifique suas credenciais.";
      toast.error(errorMessage);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-[hsl(var(--primary))]/20 via-[hsl(var(--primary))]/5 to-[hsl(var(--secondary))]/10">
      {/* Elementos decorativos de fundo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Círculos decorativos */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -top-24 -left-24 w-96 h-96 bg-[hsl(var(--primary))]/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, -90, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -bottom-24 -right-24 w-96 h-96 bg-[hsl(var(--secondary))]/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            y: [0, -20, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute top-1/2 left-1/4 w-64 h-64 bg-[hsl(var(--primary))]/5 rounded-full blur-2xl"
        />

        {/* Grid de pontos decorativo */}
        <div className="absolute inset-0 opacity-20">
          <div className="h-full w-full bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:20px_20px]" />
        </div>
      </div>

      {/* Container principal com duas colunas */}
      <div className="w-full max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center relative z-10">
        {/* Coluna esquerda - Informações */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden md:block"
        >
          <div className="space-y-8">
            <div>
              <div className="mb-2">
                <h1 className="text-6xl font-black mb-2 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] bg-clip-text text-transparent">
                  Vida+Simples
                </h1>
                <p className="text-lg font-medium text-gray-700">Ceará</p>
              </div>
              <p className="text-xl text-gray-600 leading-relaxed mt-6">
                Plataforma inteligente de gestão urbana e atendimento ao cidadão
              </p>
            </div>

            {/* Cards de features */}
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <MapPin className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Monitoramento Geolocalizado
                  </h3>
                  <p className="text-sm text-gray-600">
                    Acompanhe demandas cidadãs e serviços municipais em tempo
                    real
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <Users className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Gestão de Equipes Municipais
                  </h3>
                  <p className="text-sm text-gray-600">
                    Organize setores e distribua ordens de serviço com
                    eficiência
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <Shield className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Segurança e Transparência
                  </h3>
                  <p className="text-sm text-gray-600">
                    Gestão segura com controle de acesso e rastreabilidade total
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Coluna direita - Formulário de Login */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full"
        >
          <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
            <CardHeader className="space-y-4 pb-8">
              <div className="flex items-center justify-center mb-2">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                >
                  <img
                    src={LogoMunicipal}
                    alt="Logo Duas Vidas"
                    className="h-25 w-25"
                  />
                </motion.div>
              </div>
              <CardTitle className="text-3xl font-bold text-center text-gray-900">
                Bem-vindo de volta
              </CardTitle>
              <p className="text-center text-muted-foreground text-sm">
                Faça login para acessar o painel administrativo
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      className={`pl-10 focus-visible:ring-[hsl(var(--primary))] ${errors.email ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                      {...register("email")}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-sm text-red-500">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Senha */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Senha
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className={`pl-10 focus-visible:ring-[hsl(var(--primary))] pr-10 ${errors.password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                      {...register("password")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-500">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Botão de Login */}
                <Button
                  type="submit"
                  disabled={loginMutation.isPending}
                  className="w-full text-white cursor-pointer h-12 text-base font-medium bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] hover:from-[hsl(var(--primary))]/90 hover:to-[hsl(var(--secondary))]/90 shadow-lg"
                >
                  {loginMutation.isPending ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Entrando...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <LogIn className="h-5 w-5" />
                      Entrar no Sistema
                    </div>
                  )}
                </Button>
              </form>

              {/* Rodapé do card */}
              <div className="mt-6 pt-6 border-t text-center">
                <p className="text-sm text-muted-foreground">
                  Precisa de acesso?{" "}
                  <button className="text-[hsl(var(--primary))] font-semibold hover:underline cursor-pointer">
                    Fale com o administrador
                  </button>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Footer mobile */}
          <p className="text-center text-sm text-gray-600 mt-8 md:hidden">
            © 2026 Prefeitura Municipal de Frecheirinha - CE
          </p>
        </motion.div>
      </div>

      {/* Footer desktop */}
      <div className="absolute bottom-6 left-0 right-0 text-center hidden md:block">
        <p className="text-sm text-gray-600">
          © 2026 Prefeitura Municipal de Frecheirinha - CE
        </p>
      </div>
    </div>
  );
};
